/**
 * Variant input handling. Every variant-taking API accepts either a bare id
 * (number, numeric string, or GID) or a variant OBJECT carrying availability
 * fields — typically the variant straight from the host's own Storefront API
 * product query:
 *
 *   getVariantState({ id, availableForSale, currentlyNotInStock })
 *
 * Availability is what decides preorder state per inventory provider:
 *
 * - STOQ-inventory plans sell out-of-stock units that are still purchasable
 *   (`inventory_policy: continue`, quantity <= 0) — exactly Shopify's
 *   `currentlyNotInStock` flag.
 * - Shopify-inventory plans sell in-stock units as preorders —
 *   `availableForSale && !currentlyNotInStock`.
 *
 * Passing the consumer's own variant fields keeps the answer market-correct
 * (`@inContext` queries return per-market availability) and lets the SDK skip
 * loading the shop-wide variant id lists entirely.
 */
import { toNumericId } from './util'

/**
 * Availability fields, in either Shopify Storefront API naming
 * (`availableForSale` / `currentlyNotInStock` / `quantityAvailable`) or raw
 * inventory naming (`inventoryQuantity` / `inventoryPolicy`). Provide
 * whichever your data source has; `currentlyNotInStock` (or quantity +
 * policy) gives exact answers.
 */
export interface VariantAvailability {
  /** Storefront API: purchasable right now (true for OOS variants that allow overselling). */
  availableForSale?: boolean
  /** Storefront API: out of stock but still purchasable (`inventory_policy: continue`, qty <= 0). */
  currentlyNotInStock?: boolean
  /** Storefront API: sellable quantity (requires the inventory read scope). */
  quantityAvailable?: number | null
  /** Raw inventory quantity, if your data source has it. */
  inventoryQuantity?: number | null
  /** Raw inventory policy: `'continue'` or `'deny'` (case-insensitive). */
  inventoryPolicy?: string | null
}

/** A variant object input: an id plus any availability fields you have. */
export interface VariantObjectInput extends VariantAvailability {
  /** Numeric id, numeric string, or `gid://shopify/ProductVariant/<id>`. */
  id: number | string
}

/** What every variant-taking API accepts: a bare id or a variant object. */
export type VariantInput = number | string | VariantObjectInput

export function variantIdOf(input: VariantInput): number {
  return toNumericId(typeof input === 'object' ? input.id : input)
}

export function availabilityOf(input: VariantInput): VariantAvailability | null {
  return typeof input === 'object' ? input : null
}

function knownQuantity(a: VariantAvailability): number | undefined {
  if (typeof a.quantityAvailable === 'number') return a.quantityAvailable
  if (typeof a.inventoryQuantity === 'number') return a.inventoryQuantity
  return undefined
}

/**
 * Whether the variant is in stock (qty > 0). `undefined` when the provided
 * fields can't answer it — note `availableForSale: true` alone is ambiguous
 * (it is also true for out-of-stock variants that allow overselling).
 */
export function isInStock(a: VariantAvailability | null): boolean | undefined {
  if (a === null) return undefined
  const quantity = knownQuantity(a)
  if (quantity !== undefined) return quantity > 0
  if (a.availableForSale === false) return false
  if (a.currentlyNotInStock !== undefined) {
    return a.currentlyNotInStock ? false : (a.availableForSale === true ? true : undefined)
  }
  return undefined
}

/**
 * Whether the variant is out of stock but still purchasable
 * (`inventory_policy: continue`, qty <= 0) — the preorder-candidate test for
 * STOQ-inventory plans. `undefined` when the provided fields can't answer it.
 */
export function isOutOfStockPurchasable(a: VariantAvailability | null): boolean | undefined {
  if (a === null) return undefined
  if (a.currentlyNotInStock !== undefined) return a.currentlyNotInStock === true
  const quantity = knownQuantity(a)
  const policy = typeof a.inventoryPolicy === 'string' ? a.inventoryPolicy.toLowerCase() : undefined
  if (quantity !== undefined) {
    if (quantity > 0) return false
    if (policy !== undefined) return policy === 'continue'
    if (a.availableForSale !== undefined) return a.availableForSale
    return undefined
  }
  if (a.availableForSale === false) return false
  return undefined
}

/**
 * Stable identity key for a variant input — lets React hooks memoize on
 * content instead of object identity (inline `{ id, ... }` literals are fine).
 */
export function variantInputKey(input: VariantInput): string {
  if (typeof input !== 'object') return String(input)
  return [
    input.id,
    input.availableForSale,
    input.currentlyNotInStock,
    input.quantityAvailable,
    input.inventoryQuantity,
    input.inventoryPolicy,
  ].join('|')
}
